// Module numeric-output declarations — graph program Phase 5.
//
// THE PROBLEM. Most decision modules resolve metric meaning through their own
// manifests, engine constants, and approved-formula refs rather than through the
// governed metric registry. That is workable per module and unfalsifiable across
// modules: nothing states, for a given number a module publishes, WHETHER it is a
// registered metric, a governed contribution to a target, an internal intermediate, a
// deliberately non-additive diagnostic, or an unmigrated exception. Without that
// statement the semantic layer can never be system-wide — you cannot prove a module
// isn't quietly publishing a dollar figure outside governed identity.
//
// THE CONTRACT. Every numeric output a module publishes carries exactly one
// disposition:
//
//   registered_metric        - IS a metric in the compiled governed registry; the
//                              declaration names the metric id, and conformance
//                              resolves it (version-scoped).
//   registered_contribution  - contributes to a TARGET metric; the declaration names
//                              the targetMetricRef, and conformance resolves it in
//                              TARGET_METRIC_REGISTRY so family/sign/overlap rules
//                              apply.
//   internal_input           - an intermediate consumed only inside the module's own
//                              computation; never rendered as an executive figure.
//   non_additive_diagnostic  - published for reading but must never be summed into a
//                              target (indices, ratios, concentration measures). A
//                              reason is REQUIRED, because "do not add this" is a
//                              claim that has to be justified.
//   exempt_temporary         - not yet migrated. A reason AND an accountable role are
//                              REQUIRED so an exemption is a visible, owned debt
//                              rather than a silent gap. This is the only disposition
//                              that is meant to disappear.
//
// MIGRATION POSTURE. Declaring all 32 modules in one change would be a large
// mechanical diff with no incremental proof, so the registry starts with a verified
// first batch and every remaining module is named in
// MODULE_NUMERIC_OUTPUT_DECLARATION_BACKLOG. The conformance spec asserts that every
// module directory is either DECLARED or BACKLOGGED — so a new module cannot be added
// without a decision, and the backlog is the measured remainder rather than an
// unknown. (Same pattern as the #3688 structure-read KNOWN_KERNEL_TOUCHERS ratchet.)

export const NUMERIC_OUTPUT_DISPOSITIONS = [
  'registered_metric',
  'registered_contribution',
  'internal_input',
  'non_additive_diagnostic',
  'exempt_temporary',
] as const;

export type NumericOutputDisposition = (typeof NUMERIC_OUTPUT_DISPOSITIONS)[number];

export type ModuleNumericOutputEntry = Readonly<{
  /** The binding key the module publishes this number under. */
  numericBindingKey: string;
  disposition: NumericOutputDisposition;
  /** Required for registered_metric: the id that must resolve in the metric registry. */
  metricId?: string;
  /**
   * Optional for registered_metric: pins the compiled registry version the id
   * claims. Required in practice when the id exists at MORE THAN ONE compiled
   * version (the bridge re-mints finance identities) — an unversioned claim on
   * such an id is ambiguous and conformance fails closed.
   */
  registryVersion?: string;
  /** Required for registered_contribution: the governed target this rolls into. */
  targetMetricRef?: Readonly<{ metricId: string; registryVersion: string }>;
  /** Required for non_additive_diagnostic and exempt_temporary. */
  reason?: string;
  /** Required for exempt_temporary: who owns clearing this debt. */
  accountableRole?: string;
  /** Where the output is emitted — a real path, so a reviewer can check the claim. */
  source: string;
}>;

export type ModuleNumericOutputDeclaration = Readonly<{
  moduleId: string;
  outputs: readonly ModuleNumericOutputEntry[];
}>;

export type NumericOutputDeclarationIssue = Readonly<{
  moduleId: string;
  numericBindingKey: string;
  code:
    | 'unknown_disposition'
    | 'registered_metric_requires_metric_id'
    | 'registered_contribution_requires_target_ref'
    | 'non_additive_requires_reason'
    | 'exempt_requires_reason_and_owner'
    | 'source_required'
    | 'duplicate_binding_key';
}>;

/**
 * Pure structural validation of a declaration. Identity resolution (does the metric id
 * exist? does the target ref resolve?) is asserted by the API-side conformance spec,
 * which has the compiled registries in scope.
 */
export function validateModuleNumericOutputDeclaration(
  declaration: ModuleNumericOutputDeclaration,
): readonly NumericOutputDeclarationIssue[] {
  const issues: NumericOutputDeclarationIssue[] = [];
  const seen = new Set<string>();

  for (const entry of declaration.outputs) {
    const at = (code: NumericOutputDeclarationIssue['code']) =>
      issues.push({
        moduleId: declaration.moduleId,
        numericBindingKey: entry.numericBindingKey,
        code,
      });

    if (seen.has(entry.numericBindingKey)) at('duplicate_binding_key');
    seen.add(entry.numericBindingKey);

    if (!NUMERIC_OUTPUT_DISPOSITIONS.includes(entry.disposition)) at('unknown_disposition');
    if (!entry.source || entry.source.trim().length === 0) at('source_required');

    if (entry.disposition === 'registered_metric' && !entry.metricId) {
      at('registered_metric_requires_metric_id');
    }
    if (entry.disposition === 'registered_contribution' && !entry.targetMetricRef) {
      at('registered_contribution_requires_target_ref');
    }
    if (entry.disposition === 'non_additive_diagnostic' && !entry.reason) {
      at('non_additive_requires_reason');
    }
    if (
      entry.disposition === 'exempt_temporary' &&
      (!entry.reason || !entry.accountableRole)
    ) {
      at('exempt_requires_reason_and_owner');
    }
  }

  return issues;
}

// ---------------------------------------------------------------------------
// Batch 1 — declared from each module's own manifest, not inferred.
// ---------------------------------------------------------------------------

const CAPITAL_ALLOCATION_BINDINGS_SOURCE =
  'apps/api/src/decision-modules/capital-allocation/capital-allocation.manifest.ts' +
  '#numericBindingsProduced';

const COMMERCIAL_EFFECTIVENESS_OUTPUTS_SOURCE =
  'apps/api/src/decision-modules/commercial-effectiveness/commercial-effectiveness.rules.ts' +
  '#COMMERCIAL_EFFECTIVENESS_FORMULA_OUTPUT_RULES';

/** Ratio/percent/count/duration/per-unit readouts: non-additive by construction. */
function nonAdditive(
  numericBindingKey: string,
  reason: string,
  source: string,
): ModuleNumericOutputEntry {
  return Object.freeze({
    numericBindingKey,
    disposition: 'non_additive_diagnostic' as const,
    reason,
    source,
  });
}

/** Absolute-dollar readouts whose bridge/target relationship is not yet adjudicated. */
function exemptDollarReadout(
  numericBindingKey: string,
  accountableRole: string,
  source: string,
): ModuleNumericOutputEntry {
  return Object.freeze({
    numericBindingKey,
    disposition: 'exempt_temporary' as const,
    reason:
      'Absolute-dollar readout whose target/bridge relationship has not been ' +
      'adjudicated; enumerated as owned debt so the completeness gate measures it ' +
      'instead of letting it bypass the disposition contract.',
    accountableRole,
    source,
  });
}

const COMMERCIAL_EFFECTIVENESS_OWNER = 'commercial-effectiveness module owner';

export const MODULE_NUMERIC_OUTPUT_DECLARATIONS: readonly ModuleNumericOutputDeclaration[] =
  Object.freeze([
    Object.freeze({
      moduleId: 'capital-allocation',
      outputs: Object.freeze([
        Object.freeze({
          numericBindingKey: 'capital_freed_usd',
          disposition: 'registered_contribution' as const,
          // The manifest's own hard constraint: freed capital is CASH-track and never
          // enters an operating-income bridge. Declaring the target ref here is what
          // makes that constraint checkable from outside the module.
          targetMetricRef: Object.freeze({
            metricId: 'cash_flow_release',
            registryVersion: 'objective_value_bridge_v1',
          }),
          source:
            'apps/api/src/decision-modules/capital-allocation/capital-allocation.manifest.ts#numericBindingKey',
        }),
        nonAdditive(
          'project_npv_usd',
          'Per-project valuation used for ranking and selection; the module\'s only ' +
            'bridge-additive output is capital_freed_usd on the cash track, and summing ' +
            'project NPVs into any target would double-count valuation against realized ' +
            'cash (the manifest\'s own never-blend constraint).',
          CAPITAL_ALLOCATION_BINDINGS_SOURCE,
        ),
        nonAdditive(
          'project_irr_pct',
          'Per-project rate of return; percentages rank projects and are meaningless summed.',
          CAPITAL_ALLOCATION_BINDINGS_SOURCE,
        ),
        nonAdditive(
          'project_profitability_index',
          'Per-project ratio (value per unit of capital); an index is never additive.',
          CAPITAL_ALLOCATION_BINDINGS_SOURCE,
        ),
      ]),
    }),
    Object.freeze({
      moduleId: 'commercial-effectiveness',
      outputs: Object.freeze([
        Object.freeze({
          numericBindingKey: 'winrate_uplift_oi_usd',
          disposition: 'registered_contribution' as const,
          targetMetricRef: Object.freeze({
            metricId: 'operating_income',
            registryVersion: 'objective_value_bridge_v1',
          }),
          source:
            'apps/api/src/decision-modules/commercial-effectiveness/commercial-effectiveness.manifest.ts#numericBindingKey',
        }),
        // Ratio / percent / count / duration / per-unit readouts — non-additive by
        // construction; each renders as its own diagnostic and must never be summed
        // into a target family.
        ...[
          ['win_rate_pct', 'Win percentage; ranks periods and segments, never sums.'],
          ['value_win_rate_pct', 'Value-weighted win percentage; never sums.'],
          ['pipeline_coverage_ratio', 'Coverage ratio; never sums.'],
          ['weighted_pipeline_coverage_ratio', 'Probability-weighted ratio; never sums.'],
          ['target_pipeline_coverage_ratio', 'Reciprocal-of-win-rate ratio; never sums.'],
          [
            'revenue_per_ramped_seller_usd',
            'Per-seller density (USD per ramped seller); per-unit dollars never sum.',
          ],
          ['sales_efficiency_ratio', 'Revenue per loaded sales cost; a ratio never sums.'],
          ['funnel_stage_conversion_pct', 'Stage conversion percentage; never sums.'],
          [
            'funnel_fallout_count',
            'Stage cohort fallout; summing across stages double-counts opportunities.',
          ],
          ['average_days_in_stage', 'Average duration; averages never sum.'],
          ['average_sales_cycle_days', 'Average duration; averages never sum.'],
          ['net_revenue_retention_pct', 'Retention percentage; never sums.'],
          ['gross_revenue_retention_pct', 'Retention percentage; never sums.'],
          [
            'cross_sell_whitespace_line_count',
            'Unattached service-line count; opportunity sizing, not an additive result.',
          ],
          ['pct_reps_at_quota', 'Share of reps; never sums.'],
          ['attainment_p50_pct', 'Median attainment percentile; never sums.'],
          ['attainment_p90_pct', 'P90 attainment percentile; never sums.'],
          ['cost_of_sale_pct', 'Cost share percentage; never sums.'],
          ['quota_ote_multiple', 'Quota-to-OTE multiple; a ratio never sums.'],
          ['plan_cost_of_growth_pct', 'Plan cost share percentage; never sums.'],
          ['winrate_delta_pts', 'Win-rate delta in points; never sums into dollars.'],
          [
            'coverage_capacity_shift',
            'Capacity reallocation measure; diagnostic movement, not an additive dollar.',
          ],
          [
            'productivity_density_delta_usd',
            'Per-seller density delta (USD per seller); per-unit dollars never sum.',
          ],
        ].map(([key, reason]) =>
          nonAdditive(key, reason, COMMERCIAL_EFFECTIVENESS_OUTPUTS_SOURCE),
        ),
        // Absolute-dollar readouts pending target/bridge adjudication — visible,
        // owned debt (the contract's own exempt_temporary mechanism), not a gap the
        // completeness gate cannot see.
        ...[
          'quota_gap_usd',
          'retention_ending_revenue_usd',
          'retention_reconciliation_residual_usd',
          'cross_sell_whitespace_usd',
          'winrate_uplift_revenue_usd',
          'coverage_rebalance_revenue_delta_usd',
        ].map((key) =>
          exemptDollarReadout(
            key,
            COMMERCIAL_EFFECTIVENESS_OWNER,
            COMMERCIAL_EFFECTIVENESS_OUTPUTS_SOURCE,
          ),
        ),
      ]),
    }),
  ]);

/**
 * Modules that have NOT yet declared their numeric outputs. Every entry is a real
 * directory under apps/api/src/decision-modules, asserted by conformance. Shrinking
 * this list is the Phase 5 work; a module may never be absent from BOTH this list and
 * MODULE_NUMERIC_OUTPUT_DECLARATIONS.
 */
export const MODULE_NUMERIC_OUTPUT_DECLARATION_BACKLOG: readonly string[] = Object.freeze([
  'capacity-utilization',
  'capital-cash',
  'carrier-transportation-cost',
  'churn-reduction',
  'cost-to-serve',
  'esg-emissions',
  'finance',
  'fuel-efficiency',
  'gl-account-common-cut',
  'industry-benchmark-lens',
  'labor-arbitrage',
  'ma-synergy-tracking',
  'marketing-spend',
  'network-optimization',
  // #5864: Fusion emits scenario-keyed output figures; register the module until the
  // contract can declare those output families without inventing static binding keys.
  'org-combination',
  'org-analysis-optimization',
  'org-pattern-cost',
  'org-pattern-structure',
  'outside-contractors',
  'portfolio-capacity-allocation',
  'pricing-realization',
  'productivity-lean-ai',
  'professional-fees',
  'real-estate-footprint',
  'risk-claims',
  'segment-margin-cleanup',
  // #5081 - PLACEHOLDER DISPOSITION, not owed migration work. The semantic
  // metric query capability publishes no module-owned numeric binding: every
  // number it returns is a promoted governed fact cell carried with its own
  // per-cell receipt, and its binding keys are minted per cell rather than
  // declared. The disposition contract has no term for that today, and a
  // declaration must carry at least one output, so it is parked here until the
  // contract owner rules on a source-packet disposition.
  'semantic-metric-query',
  'sga-spend',
  'shared-services',
  'strategic-execution-capacity-diagnostic',
  'tech-software',
  'workforce-reduction',
]);
